/* eslint-disable react/jsx-no-target-blank */
import React from 'react';
import {
  IllustrationTlcAdvantage1,
  IllustrationTlcAdvantage2,
  IllustrationTlcAdvantage3,
  IllustrationTlcAdvantage4,
} from '@maiia/design-system';
import { styled, css, useTheme } from '@mui/material/styles';
import { Stack, Typography, useMediaQuery, Box } from '@mui/material';
import sanitize from 'sanitize-html';

const CARDS_DATA = [
  {
    illustration: <IllustrationTlcAdvantage1 />,
    title: 'Une solution aux problèmes de mobilité',
    content: `Très pratique pour les personnes qui ne sont pas en mesure de se déplacer, par manque de transports en commun ou par handicap physique. La visio consultation permet d’assurer une bonne qualité de soin.`,
  },
  {
    illustration: <IllustrationTlcAdvantage2 />,
    title: 'Ne plus attendre des heures en salle d’attente',
    content: `Plus besoin d’aller à l’improviste dans une salle d’attente d’un professionnel de santé et espérer qu’il n’y ait pas trop de monde. Grâce à la consultation vidéo, vous pouvez choisir un médecin ou un spécialiste avec peu d’attente et attendre tranquillement sur votre canapé le début de la consultation en ligne.`,
  },
  {
    illustration: <IllustrationTlcAdvantage3 />,
    title: 'Amélioration de la qualité des soins',
    content: `Un patient nécessitant un suivi <a href="/teleconsultation/article/pourquoi-faire-une-teleconsultation/suivi-medical-en-ligne" target='_blank' rel='noopener'>médical régulier</a>, dans le cas d'une maladie chronique, pourra bénéficier de la consultation en ligne. Faire du suivi à distance permettra d’avoir un suivi plus régulier car le professionnel de santé pourra placer des RDVs plus régulièrement. Plus besoin d’attendre 6 mois entre chaque RDV.`,
  },
  {
    illustration: <IllustrationTlcAdvantage4 />,
    title: 'Une solution face aux déserts médicaux',
    content: `Pour les patients en situation de désert médical, il peut être compliqué de trouver un spécialiste disponible. La téléconsultation vous permet d’entrer en contact avec un professionnel de santé n’importe où en France. Elle permet à tous les patients d’accéder à un bon service de soin.`,
  },
];

const CardsWrapper = styled(Box)(
  ({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-top: ${theme.spacing(4)};

    ${theme.breakpoints.up('sm')} {
      margin-top: ${theme.spacing(8)};
      align-items: baseline;
    }
  `,
);

const Card = styled(Box)(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: ${theme.spacing(6)};
    flex: 1 0 40%;

    ${theme.breakpoints.up('sm')} {
      &:nth-child(odd) {
        margin-right: ${theme.spacing(8)};
      }
    }
  `,
);

const Banner = styled(Box)(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: ${theme.palette.background.xLightPink};
    padding: ${theme.spacing(2, 3)};
    border-radius: ${theme.spacing(1.5)};
    margin-bottom: ${theme.spacing(6)};

    img {
      width: 180px;
      height: 180px;
      margin-bottom: ${theme.spacing(4)};
    }

    ${theme.breakpoints.up('sm')} {
      padding: ${theme.spacing(2, 5)};
      flex-direction: row;

      img {
        margin-bottom: unset;
        margin-right: ${theme.spacing(4)};
      }
    }
  `,
);

const TlcAdvantagesSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box className="blue-links">
      <Typography
        variant={isMobile ? 'h3' : 'h2'}
        color="primary"
        component="h2"
        textAlign={{ sm: 'center' }}
        mt={4}
      >
        Les grands avantages de la consultation médicale en ligne
      </Typography>

      <CardsWrapper>
        {CARDS_DATA.map(card => (
          <Card key={card.title}>
            {card.illustration}

            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              component="h3"
              alignSelf="start"
              mt={4}
            >
              {card.title}
            </Typography>

            <Typography
              // "dangerouslySetInnerHTML" because card 3 contains a link
              dangerouslySetInnerHTML={{
                __html: sanitize(card.content, { allowedTags: ['a'] }),
              }}
            />
          </Card>
        ))}
      </CardsWrapper>

      <Banner>
        <img src="/static/images/illu-rounded-tlc.svg" alt="Téléconsultation" />

        <Box>
          <Typography variant="h3">
            Une aide précieuse lors de crise sanitaire
          </Typography>

          <Typography>
            Pendant la crise de la{' '}
            <a
              href="/teleconsultation/article/pourquoi-faire-une-teleconsultation/tout-ce-quil-faut-savoir-sur-la-teleconsultation-covid-19"
              target="_blank"
              rel="noopener"
            >
              COVID-19
            </a>
            , la consultation vidéo a permit aux patients de continuer à obtenir
            une aide médicale, sans risque de contamination. Le fait de pouvoir
            le faire à distance à permit aux professionnels et aux patients de
            rester en sécurité sans craindre d’être contaminé en salle d’attente
            physique.
            <br />
            Pour les patients ayant besoin d’un suivi régulier, la
            téléconsultation leur a permit de continuer leur suivi médical à
            distance, et de ne pas perdre en qualité de soin à cause de la crise
            sanitaire.
          </Typography>
        </Box>
      </Banner>

      <Box mb={5}>
        <Typography variant={isMobile ? 'h6' : 'h5'} component="h4">
          Pour aller plus loin
        </Typography>

        <Stack spacing={1}>
          <a
            href="/teleconsultation/article/pourquoi-faire-une-teleconsultation/teleconsultation-donnees-de-sante"
            target="_blank"
            rel="noopener"
          >
            Téléconsultation : Protection des données de santé
          </a>
          <a
            href="/teleconsultation/article/pourquoi-faire-une-teleconsultation"
            target="_blank"
            rel="noopener"
          >
            Pourquoi faire une téléconsultation
          </a>
        </Stack>
      </Box>
    </Box>
  );
};

export default TlcAdvantagesSection;
